import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_QUERY_LENGTH } from '~/shared/const/global-search'
import { defineInjection } from '~/shared/utils/di'
import type { GlobalSearchItems } from '~/shared/interfaces/search'
import { fetchSearchItems, getFirstSearchItem, hasSearchItems } from '~/composables/global-search/search-items'

const createGlobalSearch = () => {
  const { $services } = useNuxtApp()
  const router = useRouter()

  const query = ref('')
  const items = ref<GlobalSearchItems>({})
  const isDesktopVisible = ref(false)
  const isMobileVisible = ref(false)
  const hasSearched = ref(false)

  let requestId = 0
  let debounceTimer: ReturnType<typeof setTimeout> | undefined

  const showNoResults = computed(() =>
    query.value.trim().length >= SEARCH_MIN_QUERY_LENGTH
    && hasSearched.value
    && !hasSearchItems(items.value),
  )

  const stopSearch = () => {
    clearTimeout(debounceTimer)
    debounceTimer = undefined
    requestId += 1
  }

  const reset = () => {
    items.value = {}
    hasSearched.value = false
    isDesktopVisible.value = false
  }

  const search = async () => {
    const value = query.value.trim()

    if (value.length < SEARCH_MIN_QUERY_LENGTH) {
      stopSearch()
      reset()
      return
    }

    const currentRequestId = ++requestId
    const nextItems = await fetchSearchItems($services, value)

    if (currentRequestId !== requestId) {
      return
    }

    items.value = nextItems
    hasSearched.value = true
    isDesktopVisible.value = true
  }

  const clearSearch = () => {
    stopSearch()

    query.value = ''
    isMobileVisible.value = false
    reset()
  }

  const openMobileSearch = () => {
    isMobileVisible.value = true
  }

  const closeMobileSearch = () => {
    stopSearch()
    isMobileVisible.value = false
    isDesktopVisible.value = false
  }

  const openResultLink = async (link: string) => {
    if (!link) {
      return
    }

    await router.push(link).catch(() => { })
    clearSearch()
  }

  const submitSearch = async () => {
    const firstItem = getFirstSearchItem(items.value)

    if (!firstItem) {
      return
    }

    await openResultLink(firstItem.link)
  }

  const setQuery = (value: string) => {
    query.value = value

    if (value.trim().length < SEARCH_MIN_QUERY_LENGTH) {
      stopSearch()
      reset()
      return
    }

    stopSearch()
    hasSearched.value = false

    debounceTimer = setTimeout(() => {
      void search()
    }, SEARCH_DEBOUNCE_MS)
  }

  onBeforeUnmount(stopSearch)

  return {
    query,
    items,
    showNoResults,
    isDesktopVisible,
    isMobileVisible,
    setQuery,
    clearSearch,
    openMobileSearch,
    closeMobileSearch,
    openResultLink,
    submitSearch,
  }
}

type GlobalSearchState = ReturnType<typeof createGlobalSearch>

const globalSearchInjection = defineInjection<GlobalSearchState>('global-search')

export const provideGlobalSearch = () => globalSearchInjection.provide(createGlobalSearch())

export const useGlobalSearch = (): GlobalSearchState => globalSearchInjection.use()
